import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ConfirmDialog } from "../../../../../../components/primitives/feedback/ConfirmDialog";
import { usePopupMenu } from "../../../../../../components/primitives/navigation/PopupMenu";
import { gaps } from "../../../../../../components/primitives/spacing";
import type { CorpusFeed } from "@shared/types/corpus/api.ts";
import { useFeedActions } from "./useFeedActions";

interface FeedActionsProps {
  feed: CorpusFeed;
  /** A run of the feed is in progress, so another cannot start. */
  runInProgress: boolean;
  /** Called after an action has changed the feed, so the page can load it again. */
  onChanged: () => Promise<void>;
}

/**
 * What an admin can do to a feed: run it now, pause or resume it, and, from the menu, retire it.
 * A retired feed offers nothing.
 */
export function FeedActions({ feed, runInProgress, onChanged }: FeedActionsProps) {
  const actions = useFeedActions(feed, onChanged);
  const retired = feed.state === "retired";
  const { menu, setPopupMenuAnchor } = usePopupMenu(
    retired ? [] : [[{ label: "Retire", onClick: actions.retire.confirm, color: "error" as const }]]
  );

  const error = actions.runNow.error ?? actions.togglePause.error;

  return (
    <>
      <Stack direction="row" gap={gaps.snug} alignItems="center">
        {retired ? null : (
          <>
            <Button
              variant="outlined"
              onClick={() => void actions.togglePause.handle()}
              disabled={actions.togglePause.loading}
            >
              {feed.state === "active" ? "Pause" : "Resume"}
            </Button>
            <Button
              variant="contained"
              onClick={() => void actions.runNow.handle()}
              disabled={actions.runNow.loading || runInProgress || feed.state !== "active"}
            >
              Run now
            </Button>
          </>
        )}
        {retired ? null : (
          <>
            <IconButton aria-label="More actions" onClick={setPopupMenuAnchor}>
              <MoreVertIcon />
            </IconButton>
            {menu}
          </>
        )}
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Snackbar
        open={actions.notice !== null}
        onClose={actions.dismissNotice}
        autoHideDuration={6000}
        message={actions.notice}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      <ConfirmDialog {...actions.retire.dialogProps} />
    </>
  );
}
